"use client";

import React, { useState, useEffect, useRef } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Crosshair, Globe, Activity, Target } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ElevatedIntelligenceForTheBuiltEnvironment() {
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  const [coordinates, setCoordinates] = useState({
    lat: 46.0622,
    lng: -90.0437
  });
  const sectionRef = useRef<HTMLElement>(null);
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll();
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x >= 0 && y >= 0 && x <= rect.width && y <= rect.height) {
        setMousePos({
          x,
          y
        });
        const lat = 46.0000 + y / rect.height * 0.01;
        const lng = -90.0000 + x / rect.width * 0.01;
        setCoordinates({
          lat: Number(lat.toFixed(4)),
          lng: Number(lng.toFixed(4))
        });
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  return <section data-section-id="35545" id="40-years-of-moving-earth" ref={sectionRef} className="dark bg-background text-foreground relative h-screen w-full overflow-hidden flex items-center">
      {}
      <div className="absolute inset-0 z-0">
        <video autoPlay loop muted playsInline poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575346/img/hero_poster_construction.png" className="h-full w-full object-cover opacity-60">
          <source src="/videos/hero_background.mp4" type="video/mp4" />
          {}
          <div className="h-full w-full bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575346/img/hero_poster_construction.png)] bg-cover bg-center" />
        </video>
        {}
        <div className="absolute inset-0 bg-black/40 z-10" />
        
        {}
        <div className="absolute inset-0 z-10 pointer-events-none opacity-20 bg-[linear-gradient(rgba(255,87,34,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,87,34,0.03)_1px,transparent_1px)] bg-[size:100px_100px]" />
      </div>

      {}
      <div className="pointer-events-none absolute z-20 hidden md:block transition-transform duration-100 ease-out will-change-transform text-primary/80" style={{
      transform: `translate(${mousePos.x - 24}px, ${mousePos.y - 24}px)`,
      left: 0,
      top: 0
    }}>
        <Target className="w-12 h-12 opacity-80" strokeWidth={1} />
        {}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[1px] bg-primary/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1px] h-[200px] bg-primary/20" />
      </div>

      {}
      <div className="absolute inset-0 z-20 pointer-events-none p-6 md:p-12 flex flex-col justify-between">
        <div className="flex justify-between items-start">
          <div className="w-16 h-16 border-l-2 border-t-2 border-primary/50 rounded-tl-sm" />
          <div className="w-16 h-16 border-r-2 border-t-2 border-primary/50 rounded-tr-sm" />
        </div>
        <div className="flex justify-between items-end">
          <div className="w-16 h-16 border-l-2 border-b-2 border-primary/50 rounded-bl-sm flex items-end p-2">
             <div className="font-mono text-xs text-primary/70 tracking-widest">
                HEAVY.IRON
             </div>
          </div>
          <div className="w-16 h-16 border-r-2 border-b-2 border-primary/50 rounded-br-sm flex items-end justify-end p-2">
             <div className="font-mono text-xs text-primary/70 tracking-widest">
                SITE.SYNC
             </div>
          </div>
        </div>
      </div>

      {}
      <div className="container relative z-30 mx-auto px-4 md:px-6">
        <div ref={textRef} className={`max-w-5xl transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-primary/10 border border-primary/20 backdrop-blur-sm mb-6">
            <Activity className="w-4 h-4 text-primary animate-pulse" />
            <span className="text-xs font-mono font-medium text-primary tracking-wider uppercase">Precision Site Work Active</span>
          </div>

          {}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter text-white mb-6 leading-[1.0] uppercase">DIRT.&nbsp;<span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">WORK. DONE.</span></h1>

          {}
          <div className="flex flex-col md:flex-row md:items-center gap-6 mb-10">
            <div className="flex items-center gap-4 font-mono text-sm text-primary/90 bg-black/60 backdrop-blur-md px-4 py-2 rounded-sm border border-white/10 w-fit">
              <Globe className="w-4 h-4" />
              <span className="tabular-nums">
                LAT: {coordinates.lat.toFixed(4)}° N
              </span>
              <span className="w-px h-4 bg-primary/40" />
              <span className="tabular-nums">
                LNG: {coordinates.lng.toFixed(4)}° W
              </span>
            </div>
            <p className="text-muted-foreground max-w-md text-sm md:text-base border-l-2 border-primary pl-4">Look, we don't just move dirt—we dig dreams. From surgical site clearing and foundations to the final, envy-inducing hardscaping, we deliver elite outcomes for every resident and business owner in the Northwoods. It’s world-class precision for everyone, minus the usual "dirt-guy"-induced headaches.</p>
          </div>

          {}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 border-none text-base h-14 px-8 rounded-sm font-black uppercase tracking-tight transition-all">
              <Link to="/JoesExcavatingLeadCapturePage">Get Estimate<ArrowRight className="ml-2 w-5 h-5" /></Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white hover:border-white text-base h-14 px-8 rounded-sm backdrop-blur-sm font-bold uppercase tracking-tight">
              <Link to="/SiteEngineeringHubPage">
                Explore Services
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-8 z-20">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="w-1.5 h-1.5 bg-primary/50 rounded-lg" />)}
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-primary/50 to-transparent mx-auto" />
      </div>
    </section>;
}